<script setup lang="ts">
import { ref, defineAsyncComponent, computed } from "vue";
import { container } from "tsyringe";

// adapter
import type { ClientStateManager } from "@/shared/types/adapter/client-interface";
import CloseAlertDispatcher from "@/shared/services/workers/common/CloseAlertDispatcher";

// instance
const closeAlertDispatcher = container.resolve(CloseAlertDispatcher);
const alertManager = container.resolve<ClientStateManager.AlertManager>("AlertManager");
const alertState = alertManager.getState();

type AlertComponent = typeof alertState.alertComponent | null;

const alertComponent = ref<AlertComponent>(null);
alertComponent.value = alertState.alertComponent;

const component = computed(() => {
    const alertComponentPath = alertState.alertComponent;
    return defineAsyncComponent(() => import(`../alerts/${alertComponentPath}.vue`));
});

const closeAlertModal = () => {
    const isStrong = alertState.alertProps?.isStrong;

    // 만일 외부에서 닫히면 안되는 알림창이라면 그대로 return 합니다.
    if (isStrong) {
        return;
    }
    closeAlertDispatcher.dispatch();
};
</script>

<template>
    <Teleport to="body" v-if="alertState.alertOpen">
        <div
            class="flex fixed top-0 left-0 justify-center items-center w-full xl:h-full h-lvh bg-primary-black/30 z-999"
            @click.self="closeAlertModal"
        >
            <div class="flex justify-center items-center h-auto w-fit z-1000" tabindex="-1">
                <Transition name="fade">
                    <component :is="component" />
                </Transition>
            </div>
        </div>
    </Teleport>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    transform: translateY(-20px);

    opacity: 0;
}
</style>
